// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/components/__tests__/RuleDetailPanel.test.tsx
import { X } from "@phosphor-icons/react";
import { Badge, Dialog, Flex, IconButton, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CitationInlinePreview } from "./CitationInlinePreview";
import { SourceViewerDialog } from "./SourceViewerDialog";

import {
  KnowledgeTrustLevel,
  orderCitationsPrimaryFirst,
  type KnowledgeRuleCitationResult,
  type KnowledgeRuleResult,
  type KnowledgeSourceResult,
} from "../../../../../utils/parsers/knowledge";
import { citationAnchorParam, citationAtAnchor } from "../citationAnchor";

function citationLocationLabel(citation: KnowledgeRuleCitationResult): string {
  const location = citation.location;
  if (location.cellAnchor?.range) {
    const sheet = location.cellAnchor.sheetName
      ? `${location.cellAnchor.sheetName} · `
      : "";
    return `${sheet}${location.cellAnchor.range}`;
  }
  if (location.pageNumber != null) {
    return `Page ${location.pageNumber}`;
  }
  return "Quote";
}

/**
 * Whether the mini viewer can render this citation inline — the same gate
 * CitationInlinePreview applies, which dispatches on the SOURCE FILE's mime
 * type (PDF by page, image by pin), not on the anchor alone.
 *
 * A page anchor is NOT enough: decks and word docs are extracted from the PDF
 * samba converted them into (`file.convertedKey`), so their citations carry
 * real page + word-box coordinates, while the file served for the source is
 * the ORIGINAL .pptx/.docx — the mini viewer renders nothing for it. Treating
 * those as inline-previewable made clicking the citation a dead end: no inline
 * page, and the full SourceViewerDialog (which falls back to the verbatim
 * quote) never opened. Spreadsheet cell anchors and quote-only citations go to
 * the dialog for the same reason.
 *
 * An unknown mime type (the source isn't on the loaded, paginated sources
 * page) stays previewable — the inline viewer resolves the real type itself.
 */
function canPreviewInline(
  citation: KnowledgeRuleCitationResult,
  mimeType: string | undefined,
): boolean {
  const location = citation.location;
  if (location.cellAnchor?.range) return false;
  const hasPage = location.pageNumber != null;
  const hasPin = location.rel_x != null && location.rel_y != null;
  if (!hasPage && !hasPin) return false;
  if (!mimeType) return true;
  if (mimeType === "application/pdf") return hasPage;
  if (mimeType.startsWith("image/")) return hasPin;
  return false;
}

/**
 * Right-anchored rule detail (deep-linked via ?ruleId=): full text, status,
 * confidence, usage, provenance, and the ranked citations — index 0 is the
 * primary citation (high trust first, then the most recently extracted
 * source, then relevance to the rule). The mini citation viewer renders the
 * SELECTED citation inline as the cited page with its pin (primary by
 * default); clicking a citation card swaps it into the viewer. Citations the
 * mini viewer cannot render (spreadsheet cells, quote-only, and page anchors
 * into a source served as a deck / word doc) open the full source viewer
 * dialog instead.
 */
export function RuleDetailPanel({
  rule,
  sources,
  onClose,
  citeRange,
  onCiteConsumed,
}: {
  rule: KnowledgeRuleResult | undefined;
  sources: KnowledgeSourceResult[];
  onClose: () => void;
  /** The cited anchor from the URL (cell range or page); opens the viewer on it. */
  citeRange?: string;
  /** Strip it from the URL once consumed, so back/forward does not reopen it. */
  onCiteConsumed?: () => void;
}) {
  const { t } = useTranslation("ui");
  // Seeded HERE as well as in the reset below, and that is not belt-and-braces:
  // `selectedRuleId` is initialised to `rule?._id`, so the reset block never
  // fires on first mount. A deep link seeded only there would silently do
  // nothing whenever the rule is already the one being shown -- which is every
  // arrival from a citation link.
  const [activeCitation, setActiveCitation] =
    useState<KnowledgeRuleCitationResult | null>(() =>
      citationAtAnchor(rule, citeRange),
    );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedRuleId, setSelectedRuleId] = useState(rule?._id);
  // What `cite` was last acted on, so a NEW one opens the viewer even when the
  // rule did not change. Tracked rather than compared against the URL, because
  // the URL is cleared as soon as the viewer opens.
  const [seenCiteRange, setSeenCiteRange] = useState(citeRange);
  // why: the controlled `open` is what releases the overlay. Radix only
  // REPORTS a dismissal through onOpenChange, so with a literal `open`
  // nothing ever flips and the overlay stays up intercepting clicks (staging
  // KDB e2e). BOTH dismiss paths flip this state — the Close button via
  // dismissDetail(), Escape via onOpenChange. onClose() clearing ruleId is a
  // second, independent release (the parent stops passing a rule, so the next
  // render hits `if (!rule) return null`), not the mechanism this state exists
  // for.
  const [dialogOpen, setDialogOpen] = useState(true);

  // The panel stays mounted across rule switches (deep-link navigation,
  // browser back/forward), so the selection has to reset when the rule
  // changes. Resetting it in an effect ran one render too LATE: that first
  // render already committed the NEW rule's citation at the OLD index, so the
  // mini viewer mounted on an unrelated citation and downloaded that source's
  // whole file (useSourceFileQuery) before the reset remounted it on the
  // primary. Adjusting during render is React's documented props-changed
  // pattern — the stale render is discarded instead of committed. The full
  // source viewer is closed for the same reason: leaving activeCitation set
  // reopens the PREVIOUS rule's dialog on top of the rule now being shown
  // (e.g. navigate away with the viewer open, then open any other rule).
  if (rule?._id !== selectedRuleId) {
    setSelectedRuleId(rule?._id);
    setSelectedIndex(0);
    // A deep link that changed the rule AND named a citation opens on it; any
    // other rule switch closes the viewer, for the reason above
    // (`citationAtAnchor` is null without a `cite`, which is the close).
    setActiveCitation(citationAtAnchor(rule, citeRange));
    // why: re-arm for the rule-SWITCH case, the one where the panel stays
    // mounted: dialogOpen is still false from the previous dismiss, so without
    // this the switched-to rule renders `open={false}` and nothing comes back.
    setDialogOpen(true);
  } else if (citeRange && citeRange !== seenCiteRange) {
    // The third arrival, and the one the two branches above miss: the panel is
    // ALREADY open on this rule and `cite` appears on the URL beside the
    // `ruleId` that is showing. First mount has passed and the rule did not
    // change, so nothing seeded the viewer and a citation deep link did
    // nothing at all. That is every link that opens the rule first and the
    // cited row second -- including `?tab=rules&ruleId=X&cite=Y` navigated to
    // from `?tab=rules&ruleId=X`.
    //
    // No setDialogOpen here on purpose: this branch runs only while the panel
    // is already up on this rule, and a dismiss clears ruleId (dismissDetail
    // -> onClose), so `dialogOpen` cannot be false when we arrive.
    //
    // Only a TRUTHY cite opens: `onCiteConsumed` strips it from the URL once
    // the viewer is up, and treating that removal as a change would close the
    // dialog the moment it opened.
    setActiveCitation(citationAtAnchor(rule, citeRange));
  }
  if (citeRange !== seenCiteRange) setSeenCiteRange(citeRange);

  if (!rule) return null;

  function dismissDetail() {
    setDialogOpen(false);
    onClose();
  }

  const sourceFile = (sourceId: string) =>
    sources.find((s) => s._id === sourceId)?.file;
  const sourceName = (sourceId: string) =>
    sourceFile(sourceId)?.fileName ?? "Deleted source";
  const previewable = (citation: KnowledgeRuleCitationResult) =>
    canPreviewInline(citation, sourceFile(citation.sourceId)?.mimeType);

  const citations = orderCitationsPrimaryFirst(rule.citations);
  const selectedCitation = citations[selectedIndex] ?? citations[0];

  return (
    <>
      <Dialog.Root
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) onClose();
        }}
      >
        <Dialog.Content
          className="fixed right-0 top-0 h-full max-h-full w-[420px] max-w-full overflow-y-auto rounded-none"
          aria-label={t("settings.ruleDetailPanel.ruleDetail")}
          data-testid="knowledge-rule-detail-panel"
        >
          <Flex justify="between" align="start" className="mb-2">
            <Dialog.Title size="4">
              {t("settings.ruleDetailPanel.rule")}
            </Dialog.Title>
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              aria-label={t("settings.ruleDetailPanel.closeRuleDetail")}
              onClick={dismissDetail}
              data-testid="knowledge-rule-detail-close"
            >
              <X aria-hidden="true" />
            </IconButton>
          </Flex>

          <Flex gap="2" wrap="wrap" className="mb-3">
            <Badge color="gray" variant="soft">
              {rule.status}
            </Badge>
            <Badge color="gray" variant="soft">
              {rule.category}
            </Badge>
            <Badge color="amber" variant="soft">
              {Math.round(rule.confidence * 100)}% confidence
            </Badge>
            {[rule.scope.brand, rule.scope.region, rule.scope.team]
              .filter(Boolean)
              .map((dim) => (
                <Badge key={dim} color="blue" variant="soft">
                  {dim}
                </Badge>
              ))}
            {/* Engineered claim features as their own badges, not buried in the
                content summary: the approval verdict (polarity) + risk level. */}
            {rule.claim?.polarity ? (
              <Badge
                color={
                  rule.claim.polarity === "approved"
                    ? "green"
                    : rule.claim.polarity === "prohibited"
                      ? "red"
                      : "amber"
                }
                variant="soft"
                data-testid="knowledge-rule-detail-polarity"
              >
                {rule.claim.polarity}
              </Badge>
            ) : null}
            {rule.tags?.riskLevel ? (
              <Badge
                color="orange"
                variant="soft"
                data-testid="knowledge-rule-detail-risk"
              >
                {rule.tags.riskLevel} risk
              </Badge>
            ) : null}
          </Flex>

          <Text as="p" size="2" className="mb-3">
            {rule.content}
          </Text>

          {/* Conditions attached to the claim (claim.qualifiers) shown as their
              own labeled field, not just inside the content sentence. */}
          {rule.claim?.qualifiers && rule.claim.qualifiers.length > 0 ? (
            <div
              className="mb-3"
              data-testid="knowledge-rule-detail-conditions"
            >
              <Text as="p" size="1" weight="bold" className="mb-1">
                {t("settings.ruleDetailPanel.conditions")}
              </Text>
              <ul aria-label={t("settings.ruleDetailPanel.conditions")}>
                {rule.claim.qualifiers.map((q, i) => (
                  <li
                    key={i}
                    data-testid={`knowledge-rule-detail-condition-${i}`}
                  >
                    <Text as="span" size="2">
                      {q}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Lossless per-column source detail (sourceFields) as a definition
              list, so every original spreadsheet column is visible + separate. */}
          {rule.sourceFields && Object.keys(rule.sourceFields).length > 0 ? (
            <details
              className="mb-3"
              data-testid="knowledge-rule-detail-source-fields"
            >
              <summary>
                <Text size="1" weight="bold">
                  {t("settings.ruleDetailPanel.sourceColumns")}
                </Text>
              </summary>
              <dl className="mt-1">
                {Object.entries(rule.sourceFields).map(([col, value]) => (
                  <div
                    key={col}
                    className="mb-1"
                    data-testid={`knowledge-rule-detail-source-field-${col}`}
                  >
                    <Text as="span" size="1" weight="bold" color="gray">
                      {col}:{" "}
                    </Text>
                    <Text as="span" size="1">
                      {value}
                    </Text>
                  </div>
                ))}
              </dl>
            </details>
          ) : null}

          <Text
            as="p"
            size="1"
            color="gray"
            className="mb-1"
            data-testid="knowledge-rule-detail-usage"
          >
            {rule.usage.reviewCount > 0
              ? `Used in ${rule.usage.reviewCount} ${
                  rule.usage.reviewCount === 1 ? "review" : "reviews"
                }${
                  rule.usage.lastUsedInReviewAt
                    ? ` · last used ${new Date(
                        rule.usage.lastUsedInReviewAt,
                      ).toLocaleDateString()}`
                    : ""
                }`
              : t("settings.ruleDetailPanel.notUsedReviewYet")}
          </Text>
          <Text as="p" size="1" color="gray" className="mb-3">
            Extracted by Puntt
            {rule.createdAt
              ? ` · ${new Date(rule.createdAt).toLocaleDateString()}`
              : ""}
            {` · confirmed ${rule.confirmationCount} ${
              rule.confirmationCount === 1 ? "time" : "times"
            }`}
          </Text>

          <Text as="p" size="2" weight="bold" className="mb-1">
            {t("settings.ruleDetailPanel.citations")}
          </Text>
          {/* Only mount the mini viewer for a citation it can actually render:
              a deck's page anchor would otherwise download the whole original
              .pptx just to render nothing. */}
          {selectedCitation && previewable(selectedCitation) ? (
            <div className="mb-2">
              <CitationInlinePreview
                // Remount on selection change so the pdf.js viewer re-opens
                // at the newly selected citation's page instead of keeping
                // the previous scroll/page state.
                key={`${selectedCitation.sourceId}-${selectedIndex}`}
                citation={selectedCitation}
                fileName={sourceName(selectedCitation.sourceId)}
              />
            </div>
          ) : null}
          <Flex direction="column" gap="2">
            {citations.map((citation, index) => (
              <button
                key={`${citation.extractionId ?? citation.sourceId}-${index}`}
                type="button"
                onClick={() => {
                  if (previewable(citation)) {
                    setSelectedIndex(index);
                  } else {
                    setActiveCitation(citation);
                  }
                }}
                aria-label={
                  previewable(citation)
                    ? `View citation ${index + 1} from ${sourceName(
                        citation.sourceId,
                      )} in the preview`
                    : `Open citation ${index + 1} in ${sourceName(
                        citation.sourceId,
                      )}`
                }
                aria-current={index === selectedIndex ? "true" : undefined}
                className={`cursor-pointer rounded border border-solid bg-transparent p-2 text-left ${
                  index === selectedIndex ? "border-blue-8" : "border-gray-5"
                }`}
                data-testid={`knowledge-rule-citation-${rule._id}-${index}`}
                // The value a deep link to THIS citation carries. Rendered so
                // the anchor is readable from the DOM rather than reconstructed
                // by whoever links here -- one definition, used by the link and
                // by the panel that resolves it.
                data-cite={citationAnchorParam(citation)}
              >
                <Flex gap="2" align="center" wrap="wrap" className="mb-1">
                  {index === 0 ? (
                    <Badge color="blue" variant="soft">
                      {t("settings.ruleDetailPanel.primary")}
                    </Badge>
                  ) : null}
                  <Badge
                    color={
                      citation.trustLevel === KnowledgeTrustLevel.HIGH
                        ? "green"
                        : "gray"
                    }
                    variant="soft"
                  >
                    {citation.trustLevel === KnowledgeTrustLevel.HIGH
                      ? t("settings.ruleDetailPanel.highTrust")
                      : t("settings.ruleDetailPanel.lowTrust")}
                  </Badge>
                  {citation.similarity != null ? (
                    <Badge
                      color="violet"
                      variant="soft"
                      title={t(
                        "settings.ruleDetailPanel.howCloselyCitationMatchesRule",
                      )}
                      data-testid={`knowledge-rule-citation-relevance-${rule._id}-${index}`}
                    >
                      {Math.round(citation.similarity * 100)}% match
                    </Badge>
                  ) : null}
                  <Text size="1" color="gray">
                    {sourceName(citation.sourceId)} ·{" "}
                    {citationLocationLabel(citation)}
                    {citation.extractedAt
                      ? ` · ${new Date(
                          citation.extractedAt,
                        ).toLocaleDateString()}`
                      : ""}
                  </Text>
                </Flex>
                <Text size="1" color="gray" className="italic">
                  “{citation.quote}”
                </Text>
              </button>
            ))}
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <SourceViewerDialog
        citation={activeCitation}
        fileName={
          activeCitation ? sourceName(activeCitation.sourceId) : undefined
        }
        onClose={() => {
          setActiveCitation(null);
          // Otherwise back/forward re-reads the URL and reopens the viewer the
          // reader just closed.
          onCiteConsumed?.();
        }}
      />
    </>
  );
}
